import { ActionIcon, Group, rem, Stack, Text, Title } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { modals } from "@mantine/modals";
import { IconFileDescription, IconTrash, IconUpload, IconX } from "@tabler/icons-react";
import { fileApi } from "shared/api/fileApi";
import { FileItem } from "shared/components/global/FileItem";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useFormState } from "shared/hooks/useFormState";
import { useFormSubscription } from "shared/hooks/useFormSubscription";
import { to } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { useUploadQueue } from "shared/utils/upload";

export const AttachmentEditor = () => {
    const form = useComposeFormContext();
    const attachmentIds = useFormSubscription(form, "files").map(to("id"));
    const [mode] = useFormState(form, "meta.mode");

    const uploader = useUploadQueue({
        onUploaded(attachments) {
            form.setFieldValue("files", (prev) => prev.concat(attachments));
        },
    });

    function handleDeleteAttachment({ attachmentId }: { attachmentId: string }) {
        modals.openConfirmModal({
            title: <Title order={5}>Delete this Attachment?</Title>,
            children: <Text>{`Are you sure you want to delete this attachment?`}</Text>,
            labels: {
                confirm: "Delete",
                cancel: "Cancel",
            },
            confirmProps: {
                color: "red",
                leftSection: <IconTrash size={16} />,
            },
            onConfirm() {
                fileApi
                    .delete(attachmentId)
                    .then(() => form.setFieldValue("files", (files) => files.filter(({ id }) => id != attachmentId)))
                    .catch(logger.error);
            },
        });
    }

    return (
        <Stack>
            {mode != "read" && (
                <Dropzone
                    multiple
                    loading={uploader.isLoading}
                    accept={[MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.svg, MIME_TYPES.gif]}
                    inputProps={{ "aria-label": "Attach files" }}
                    onDrop={(files) => {
                        files.forEach(uploader.enqueue);
                    }}
                >
                    <Stack align="center" gap={"xs"} py="xl">
                        <Dropzone.Accept>
                            <IconUpload style={{ width: rem(42), height: rem(42) }} stroke={1.5} />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                            <IconX style={{ width: rem(42), height: rem(42), color: "red" }} stroke={1.5} />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                            <IconFileDescription style={{ width: rem(42), height: rem(42), color: "dimmed" }} stroke={1.5} />
                        </Dropzone.Idle>

                        <div style={{ textAlign: "center" }}>
                            <Text size="lg" inline>
                                Attach Files
                            </Text>
                            <Text size="sm" c="dimmed" inline mt={7}>
                                pdf, jpeg, png, svg, or gif
                            </Text>
                        </div>
                    </Stack>
                </Dropzone>
            )}

            <Group>
                {attachmentIds.map((attachmentId) => (
                    <FileItem key={attachmentId} fileId={attachmentId}>
                        <FileItem.Action>
                            <ActionIcon
                                size="xs"
                                variant="transparent"
                                onClick={() => handleDeleteAttachment({ attachmentId })}
                                aria-label="Remove attachment"
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </FileItem.Action>
                    </FileItem>
                ))}
            </Group>
        </Stack>
    );
};
